import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useHover,
  useInteractions,
  FloatingArrow,
  arrow,
} from '@floating-ui/react';
import { useState, useRef } from 'react';
import { Handle, Position } from '@xyflow/react';
import { EntityListResp, SupportStatus } from '@/types/home';
import { formatAmountSum } from '@/utils';
import { useFm } from '@web3/base-hooks';

// TODO 要和详情页保持一致
// 最终是三种状态 2 完全成功 1 部分成功 0 失败
const supportStatusMap = {
  [SupportStatus.NO_RESP]: 0,
  [SupportStatus.PARTIAL_SUPPORT]: 1,
  [SupportStatus.REJECT_PROVIDE]: 0,
  [SupportStatus.ALL_LOST]: 0,
  [SupportStatus.FREEZED]: 2,
  [SupportStatus.PROVIDED_NEXT_ADDRESS]: 2,
  [SupportStatus.FULL_PROVIDED]: 2,
};

const getPercentage = (numerator: string, denominator: string): string => {
  try {
    const num = Number(numerator);
    const den = Number(denominator);

    // 处理分母为0或非数字的情况
    if (!den || isNaN(den)) {
      return '0%';
    }

    // 处理分子为非数字的情况
    if (isNaN(num)) {
      return '0%';
    }

    // 计算百分比并保留2位小数
    const percentage = ((num / den) * 100).toFixed(2);
    return `${percentage}%`;
  } catch (error) {
    return '0%';
  }
};

export function EntityNode({
  data,
  curEntity,
}: {
  data: EntityListResp;
  curEntity: EntityListResp;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const arrowRef = useRef(null);
  const fm = useFm();

  const support = {
    [SupportStatus.ALL_LOST]: {
      color: '#EF454A',
      bgColor: 'rgba(239, 69, 74, 0.16)',
      text: fm('allLost'),
    },
    [SupportStatus.NO_RESP]: {
      color: '#EF454A',
      bgColor: 'rgba(239, 69, 74, 0.16)',
      text: fm('noResponse'),
    },
    [SupportStatus.REJECT_PROVIDE]: {
      color: '#EF454A',
      bgColor: 'rgba(239, 69, 74, 0.16)',
      text: fm('rejectSupport'),
    },
    [SupportStatus.FREEZED]: {
      color: '#20B26C',
      bgColor: 'rgba(32, 178, 108, 0.16)',
      text: fm('frozen'),
    },
    [SupportStatus.PROVIDED_NEXT_ADDRESS]: {
      color: '#20B26C',
      bgColor: 'rgba(32, 178, 108, 0.16)',
      text: fm('providedNextDest'),
    },
    [SupportStatus.FULL_PROVIDED]: {
      color: '#20B26C',
      bgColor: 'rgba(32, 178, 108, 0.16)',
      text: fm('fullySupported'),
    },
    [SupportStatus.PARTIAL_SUPPORT]: {
      color: '#F7A600',
      bgColor: 'rgba(247, 166, 0, 0.16)',
      text: fm('partiallySupported'),
    },
  };

  const { x, y, strategy, refs, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    middleware: [offset(10), flip(), shift(), arrow({ element: arrowRef })],
    whileElementsMounted: autoUpdate,
    placement: 'right-start',
  });

  const hover = useHover(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([hover]);

  return (
    <>
      <div ref={refs.setReference} {...getReferenceProps()}>
        <div>
          <Handle
            type="target"
            position={Position.Left}
            style={{ background: '#555', opacity: 0 }}
          />
          <div
            className={`w-[264px] h-[80px] flex flex-col justify-center px-[16px] rounded-xl
              ${
                supportStatusMap[data.supportStatus] === 2
                  ? 'bg-[rgb(32_178_108/0.12)]'
                  : supportStatusMap[data.supportStatus] === 1
                    ? 'bg-[rgb(247_166_0/0.12)]'
                    : 'bg-[rgb(239_69_74/0.16)]'
              }
              ${curEntity?.name === data.name ? 'border-[#FFFEFE]' : isOpen ? 'border-[#595D61]' : 'border-transparent'}
              border-[1px]
            `}
          >
            {[
              {
                label: fm('endPoint'),
                value: data.name,
              },
              {
                label: fm('status'),
                value: support[data.supportStatus].text,
              },
              {
                label: fm('totalUnblockedFunds'),
                value: formatAmountSum(data.received),
              },
            ].map(({ label, value }) => (
              <div key={label} className="flex justify-between items-center">
                <div className="text-[#ADB1B8] font-ibm-plex-sans text-xs font-medium leading-normal">
                  {label}
                </div>
                {label === fm('endPoint') ? (
                  <div className="flex items-center">
                    <img
                      src={data.logo}
                      alt={data.name}
                      className="w-[12px] h-[12px] mr-1 rounded-full flex-shrink-0 aspect-[16.67/16.67] border-[0.6px] border-[#595D61]"
                    />
                    <div className="text-white font-ibm-plex-sans text-xs font-medium truncate max-w-[180px]">
                      {value}
                    </div>
                  </div>
                ) : (
                  <div
                    className={`text-white text-right font-ibm-plex-sans text-xs font-medium truncate max-w-[180px]`}
                  >
                    {value}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {isOpen && (
        <div
          ref={refs.setFloating}
          style={{
            position: strategy,
            top: y ?? 0,
            left: x ?? 0,
            width: 'max-content',
          }}
          {...getFloatingProps()}
          className="bg-[#404347] p-3 rounded-lg"
        >
          <FloatingArrow
            ref={arrowRef}
            context={context}
            tipRadius={1}
            strokeWidth={1}
            fill="#404347"
          />
          <div className="flex flex-col items-start gap-1 min-w-[170px]">
            <div className="text-white font-ibm-plex-sans text-xs font-semibold leading-[18px]">
              {fm('summary')}
            </div>
            <div className="flex flex-col gap-1 w-full">
              {[
                data.supportStatus === SupportStatus.NO_RESP ||
                data.supportStatus === SupportStatus.PARTIAL_SUPPORT ||
                data.supportStatus === SupportStatus.REJECT_PROVIDE
                  ? {
                      label: fm('fundAwaitResponse'),
                      value: formatAmountSum(data.unblocked),
                    }
                  : null,
                {
                  label: fm('lostFunds'),
                  value: formatAmountSum(data.lostFund),
                },
                {
                  label: fm('blockedFunds'),
                  value: formatAmountSum(data.blocked),
                },
                {
                  label: fm('outflowedFunds'),
                  value: formatAmountSum(data.backOnChainFunds),
                },
                {
                  label: fm('totalEstimatedBountyRewards'),
                  value: formatAmountSum(data.estimatedBountyAmount),
                },
              ].map((item) => {
                if (!item) return null;
                const { label, value } = item;
                return (
                  <div
                    className="flex justify-between items-center"
                    key={label}
                  >
                    <span className="text-[#ADB1B8] font-ibm-plex-sans text-xs font-medium">
                      {label}
                    </span>
                    <span className="text-white font-ibm-plex-sans text-xs font-medium ml-6">
                      {value}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

interface BaseWalletNodeProps {
  children: React.ReactNode;
  background: string;
  showSourceHandle?: boolean;
  showTargetHandle?: boolean;
}

function BaseWalletNode({
  children,
  background,
  showSourceHandle = false,
  showTargetHandle = false,
}: BaseWalletNodeProps) {
  return (
    <div
      className="flex w-[160px] h-[84px] justify-center items-center gap-[10px] flex-shrink-0 rounded-lg relative"
      style={{
        background,
        color: '#000',
        fontFamily: 'IBM Plex Sans',
        fontSize: '16px',
        fontWeight: 600,
        lineHeight: 'normal',
      }}
    >
      {showTargetHandle && (
        <Handle
          type="target"
          position={Position.Left}
          style={{ background: '#555', opacity: 0 }}
        />
      )}
      {children}
      {showSourceHandle && (
        <Handle
          type="source"
          position={Position.Right}
          style={{ background: '#555', opacity: 0 }}
        />
      )}
    </div>
  );
}

export function BybitWalletNode() {
  const fm = useFm();

  return (
    <BaseWalletNode
      background="linear-gradient(133deg, #FFD748 -1.87%, #F7A600 101.87%)"
      showSourceHandle
    >
      💰 {fm('wallet')}
    </BaseWalletNode>
  );
}

export function HackerWalletsNode() {
  const fm = useFm();
  return (
    <BaseWalletNode background="#ADB1B8" showSourceHandle showTargetHandle>
      🏴‍☠️ {fm('lazarusWallets')}
    </BaseWalletNode>
  );
}
